import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Mail, Play, Pause, Lock, Settings, Eye, Trash2, MapPin, AlertTriangle, Loader2, RefreshCw, X, Edit2 } from 'lucide-react';
import { crmMailingListsApi, CrmMailingList } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function MailingLists() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [lists, setLists] = useState<CrmMailingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState<string | null>(null);
  const [intakeMessage, setIntakeMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state for create
  const [formData, setFormData] = useState({
    Name: '',
    Description: '',
    Status: 'open',
    Intake_Limit: ''
  });
  // Derive team from workspace scope
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const fetchLists = async () => {
    setLoading(true);
    try {
      const data = await crmMailingListsApi.list();
      setLists(data);
    } catch (err) {
      console.error('Failed to fetch lists:', err);
      toast.error('Failed to load mailing lists');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchLists();
  }, []);
  // Filter by team and region scope
  const filteredLists = lists.filter(l => {
    // Team filter: only show lists belonging to current workspace or with no team
    if (l.Team && l.Team !== currentWorkspace.name && l.Team !== team) return false;
    // Region filter
    if (currentSubRegion) {
      return !l.Region || l.Region === currentSubRegion;
    }
    return true;
  });
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.Name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: Partial<CrmMailingList> = {
        Name: formData.Name,
        Description: formData.Description,
        Status: formData.Status,
        Intake_Limit: formData.Intake_Limit ? parseInt(formData.Intake_Limit) : undefined,
        Team: team
      };
      if (currentSubRegion) {
        payload.Region = currentSubRegion;
      }
      await crmMailingListsApi.create(payload);
      toast.success('Mailing list created successfully');
      setShowCreateModal(false);
      setFormData({
        Name: '',
        Description: '',
        Status: 'open',
        Intake_Limit: ''
      });
      fetchLists();
    } catch (err) {
      console.error('Failed to create list:', err);
      toast.error('Failed to create mailing list');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    try {
      await crmMailingListsApi.delete(id);
      toast.success('Mailing list deleted');
      setDeleteConfirm(null);
      fetchLists();
    } catch (err) {
      console.error('Failed to delete list:', err);
      toast.error('Failed to delete mailing list');
    }
  };
  const handleStatusChange = async (id: string, newStatus: string, message?: string) => {
    try {
      const payload: Partial<CrmMailingList> = {
        Status: newStatus
      };
      if (newStatus === 'paused' && message) payload.Paused_Message = message;
      if (newStatus === 'closed' && message) payload.Closed_Message = message;
      await crmMailingListsApi.update(id, payload);
      toast.success(`List status updated to ${newStatus}`);
      fetchLists();
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update list status');
    }
  };
  const openIntakeModal = (id: string, status: 'paused' | 'closed') => {
    setShowIntakeModal(id);
    // Store intended status in a way we can retrieve it, or just handle in the modal logic
    // For simplicity, we'll assume the modal knows which status to apply based on button click
  };
  const getIntakeStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return {
          icon: Play,
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-200',
          label: 'Accepting Signups'
        };
      case 'paused':
        return {
          icon: Pause,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          label: 'Paused'
        };
      case 'closed':
        return {
          icon: Lock,
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          label: 'Closed'
        };
      default:
        return {
          icon: Play,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          label: 'Unknown'
        };
    }
  };
  // Stats calculation
  const totalSubscribers = filteredLists.reduce((sum, list) => sum + (list.Subscriber_Count || 0), 0);
  const openLists = filteredLists.filter(l => l.Status === 'open').length;
  const pausedLists = filteredLists.filter(l => l.Status === 'paused').length;
  return <div className="space-y-6" data-id="element-4957">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4958">
        <div className="min-w-0" data-id="element-4959">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4960">
            Mailing Lists
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4961">
            Manage your audience lists, intake controls, and subscribers (synced
            with Zoho CRM).
            {currentSubRegion && <span className="ml-1 font-medium text-blue-600" data-id="element-4962">
                Filtered by: {currentSubRegion}
              </span>}
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-4963">
          <button onClick={fetchLists} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-4964">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4965" />
          </button>
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-4966">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-4967" />
            <span data-id="element-4968">Create List</span>
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4969">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4970">
          <div className="flex items-center gap-3" data-id="element-4971">
            <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-4972">
              <Mail className="h-5 w-5 text-blue-600" data-id="element-4973" />
            </div>
            <div className="min-w-0" data-id="element-4974">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4975">
                {filteredLists.length}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-4976">Total Lists</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4977">
          <div className="flex items-center gap-3" data-id="element-4978">
            <div className="p-2 bg-green-50 rounded-lg flex-shrink-0" data-id="element-4979">
              <Play className="h-5 w-5 text-green-600" data-id="element-4980" />
            </div>
            <div className="min-w-0" data-id="element-4981">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4982">
                {openLists}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-4983">
                Accepting Signups
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4984">
          <div className="flex items-center gap-3" data-id="element-4985">
            <div className="p-2 bg-yellow-50 rounded-lg flex-shrink-0" data-id="element-4986">
              <Pause className="h-5 w-5 text-yellow-600" data-id="element-4987" />
            </div>
            <div className="min-w-0" data-id="element-4988">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4989">
                {pausedLists}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-4990">Paused</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4991">
          <div className="flex items-center gap-3" data-id="element-4992">
            <div className="p-2 bg-purple-50 rounded-lg flex-shrink-0" data-id="element-4993">
              <Users className="h-5 w-5 text-purple-600" data-id="element-4994" />
            </div>
            <div className="min-w-0" data-id="element-4995">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4996">
                {totalSubscribers.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-4997">
                Total Subscribers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lists Grid */}
      {loading && filteredLists.length === 0 ? <div className="p-12 text-center bg-white border border-gray-200 rounded-lg" data-id="element-4998">
          <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4999" />
          <p className="mt-2 text-sm text-gray-500" data-id="element-5000">Loading mailing lists...</p>
        </div> : filteredLists.length === 0 ? <div className="p-12 text-center bg-white border border-gray-200 rounded-lg" data-id="element-5001">
          <Mail className="mx-auto h-12 w-12 text-gray-400" data-id="element-5002" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5003">
            No mailing lists found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5004">
            {currentSubRegion ? `No lists found for region "${currentSubRegion}".` : 'Create a list to start collecting subscribers.'}
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-5005">
          {filteredLists.map(list => {
        const intakeConfig = getIntakeStatusConfig(list.Status);
        const IntakeIcon = intakeConfig.icon;
        const subscriberCount = list.Subscriber_Count || 0;
        const intakeLimit = list.Intake_Limit;
        const isNearLimit = intakeLimit && subscriberCount >= intakeLimit * 0.9;
        return <div key={list.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" data-id="element-5006">
                {/* Header */}
                <div className={`px-4 py-3 ${intakeConfig.bg} border-b ${intakeConfig.border} flex-shrink-0`} data-id="element-5007">
                  <div className="flex items-center justify-between gap-2" data-id="element-5008">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-5009">
                      <IntakeIcon className={`h-4 w-4 ${intakeConfig.color} flex-shrink-0`} data-id="element-5010" />
                      <span className={`text-sm font-medium ${intakeConfig.color} truncate`} data-id="element-5011">
                        {intakeConfig.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" data-id="element-5012">
                      {list.Status !== 'open' && <button onClick={() => handleStatusChange(list.id, 'open')} className="p-1 hover:bg-white/50 rounded text-green-600" title="Open Intake" data-id="element-5013">
                          <Play className="h-4 w-4" data-id="element-5014" />
                        </button>}
                      {list.Status !== 'paused' && <button onClick={() => {
                  setShowIntakeModal(list.id);
                  // We'll handle the actual status change in the modal
                }} className="p-1 hover:bg-white/50 rounded text-yellow-600" title="Pause Intake" data-id="element-5015">
                          <Pause className="h-4 w-4" data-id="element-5016" />
                        </button>}
                      {list.Status !== 'closed' && <button onClick={() => {
                  setShowIntakeModal(list.id);
                  // We'll handle the actual status change in the modal
                }} className="p-1 hover:bg-white/50 rounded text-red-600" title="Close Intake" data-id="element-5017">
                          <Lock className="h-4 w-4" data-id="element-5018" />
                        </button>}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4 flex-1" data-id="element-5019">
                  <div className="min-w-0" data-id="element-5020">
                    <h3 className="text-lg font-medium text-gray-900 truncate" data-id="element-5021">
                      {list.Name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2" data-id="element-5022">
                      {list.Description || 'No description'}
                    </p>
                  </div>

                  {/* Subscriber Count */}
                  <div className="space-y-2" data-id="element-5023">
                    <div className="flex items-center justify-between text-sm" data-id="element-5024">
                      <span className="text-gray-600" data-id="element-5025">Subscribers</span>
                      <span className="font-semibold text-gray-900" data-id="element-5026">
                        {subscriberCount.toLocaleString()}
                      </span>
                    </div>
                    {intakeLimit && <>
                        <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-5027">
                          <div className={`h-2 rounded-full ${isNearLimit ? 'bg-yellow-500' : 'bg-blue-500'}`} style={{
                    width: `${Math.min(subscriberCount / intakeLimit * 100, 100)}%`
                  }} data-id="element-5028" />
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-5029">
                          <span data-id="element-5030">Limit: {intakeLimit.toLocaleString()}</span>
                          {isNearLimit && <span className="flex items-center gap-1 text-yellow-600" data-id="element-5031">
                              <AlertTriangle className="h-3 w-3 flex-shrink-0" data-id="element-5032" />
                              <span className="whitespace-nowrap" data-id="element-5033">
                                Near capacity
                              </span>
                            </span>}
                        </div>
                      </>}
                  </div>

                  {/* Meta Badges */}
                  <div className="flex flex-wrap gap-2 text-xs" data-id="element-5034">
                    {list.Region && <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded whitespace-nowrap" data-id="element-5035">
                        <MapPin className="h-3 w-3 flex-shrink-0" data-id="element-5036" />
                        {list.Region}
                      </span>}
                    {list.Team && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded whitespace-nowrap" data-id="element-5037">
                        <Users className="h-3 w-3 flex-shrink-0" data-id="element-5038" />
                        {list.Team}
                      </span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-5039">
                  <Link to={`/mailing-lists/${list.id}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap" data-id="element-5040">
                    <Eye className="h-4 w-4 flex-shrink-0" data-id="element-5041" />
                    <span data-id="element-5042">View Details</span>
                  </Link>
                  <div className="flex items-center gap-2" data-id="element-5043">
                    <button onClick={() => setDeleteConfirm(list.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-5044">
                      <Trash2 className="h-4 w-4" data-id="element-5045" />
                    </button>
                  </div>
                </div>
              </div>;
      })}
        </div>}

      {/* Create Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5046">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5047">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} data-id="element-5048" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-5049">
              <div className="flex items-center justify-between mb-4" data-id="element-5050">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5051">
                  Create Mailing List
                </h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5052">
                  <X className="h-5 w-5" data-id="element-5053" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="space-y-4" data-id="element-5054">
                <div data-id="element-5055">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5056">
                    List Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Name} onChange={e => setFormData({
                ...formData,
                Name: e.target.value
              })} data-id="element-5057" />
                </div>
                <div data-id="element-5058">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5059">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Description} onChange={e => setFormData({
                ...formData,
                Description: e.target.value
              })} data-id="element-5060" />
                </div>
                <div data-id="element-5061">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5062">
                    Intake Limit (Optional)
                  </label>
                  <input type="number" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Intake_Limit} onChange={e => setFormData({
                ...formData,
                Intake_Limit: e.target.value
              })} data-id="element-5063" />
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5064">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5065">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting || !formData.Name.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-5066">
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5067" />}
                    Create List
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Intake Message Modal */}
      {showIntakeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5068">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5069">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowIntakeModal(null)} data-id="element-5070" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-5071">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-5072">
                Configure Intake Message
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-5073">
                This message will be shown to users when they try to sign up
                while the list is paused or closed.
              </p>
              <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="e.g., We're currently at capacity. Please check back soon!" value={intakeMessage} onChange={e => setIntakeMessage(e.target.value)} data-id="element-5074" />
              <div className="flex justify-end gap-3 mt-4" data-id="element-5075">
                <button onClick={() => setShowIntakeModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5076">
                  Cancel
                </button>
                <div className="flex gap-2" data-id="element-5077">
                  <button onClick={() => {
                if (showIntakeModal) handleStatusChange(showIntakeModal, 'paused', intakeMessage);
                setShowIntakeModal(null);
              }} className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700" data-id="element-5078">
                    Pause List
                  </button>
                  <button onClick={() => {
                if (showIntakeModal) handleStatusChange(showIntakeModal, 'closed', intakeMessage);
                setShowIntakeModal(null);
              }} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-5079">
                    Close List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5080">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5081">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-5082" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-5083">
              <div className="flex items-center gap-4 mb-4" data-id="element-5084">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5085">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-5086" />
                </div>
                <div data-id="element-5087">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5088">
                    Delete List
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-5089">
                    Are you sure? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-5090">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5091">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-5092">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}